import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { useWorkspaceStore, LAYOUT_CONFIGS, WORKSPACE_PRESETS } from '@/hooks/useWorkspace'
import { useMultiTerminalStore } from '@/hooks/useTerminal'
import type { WorkspaceLayout, PanelConfig, TerminalLine } from '@/lib/types'

// ── All commands for autocomplete ────────────────────────────────────────

const ALL_COMMANDS = [
  // Equity
  { cmd: '/quote', alias: '/q BQ QR', desc: 'Real-time quote' },
  { cmd: '/chart', alias: '/c GP GIP COMP', desc: 'Chart' },
  { cmd: '/profile', alias: '/p DES FA', desc: 'Company profile' },
  { cmd: '/earnings', alias: '/e ERN EA', desc: 'Earnings' },
  { cmd: '/dividends', alias: '/div DVD', desc: 'Dividends' },
  { cmd: '/technicals', alias: 'TA', desc: 'Technical analysis' },
  { cmd: '/compare', alias: null, desc: 'Compare stocks' },
  { cmd: '/options', alias: '/opt', desc: 'Options chain' },
  // Valuation
  { cmd: '/wacc', alias: null, desc: 'WACC' },
  { cmd: '/dcf', alias: null, desc: 'DCF valuation' },
  { cmd: '/ddm', alias: null, desc: 'Dividend discount' },
  { cmd: '/financials', alias: 'FA', desc: 'Financials' },
  { cmd: '/peers', alias: null, desc: 'Peer comparison' },
  { cmd: '/targets', alias: 'TGT', desc: 'Price targets' },
  { cmd: '/ratings', alias: 'RATD', desc: 'Analyst ratings' },
  // Market
  { cmd: '/indices', alias: '/idx WB INDU NKY DAX', desc: 'Market indices' },
  { cmd: '/movers', alias: 'MOV', desc: 'Top movers' },
  { cmd: '/sectors', alias: 'S5SECT', desc: 'Sector performance' },
  { cmd: '/bonds', alias: 'GB', desc: 'Treasury yields' },
  { cmd: '/vix', alias: null, desc: 'VIX index' },
  { cmd: '/forex', alias: '/fx FXIP FXC', desc: 'Forex rates' },
  { cmd: '/crncy', alias: 'FXCM', desc: 'FX matrix' },
  { cmd: '/commodities', alias: 'CMD CMDTY', desc: 'Commodities' },
  { cmd: '/country', alias: null, desc: 'Country dashboard' },
  { cmd: '/crypto', alias: 'CTOP', desc: 'Crypto' },
  { cmd: '/topvol', alias: null, desc: 'Top volume' },
  // Alt Data
  { cmd: '/insider', alias: 'ANR BI', desc: 'Insider trades' },
  { cmd: '/congress', alias: null, desc: 'Congressional trades' },
  { cmd: '/guru', alias: null, desc: 'Guru portfolios' },
  { cmd: '/shorts', alias: 'SI MOST', desc: 'Short interest' },
  { cmd: '/squeeze', alias: null, desc: 'Squeeze candidates' },
  { cmd: '/sentiment', alias: 'SALT', desc: 'Social sentiment' },
  { cmd: '/wsb', alias: null, desc: 'WSB trending' },
  { cmd: '/fear', alias: null, desc: 'Fear & Greed' },
  { cmd: '/flows', alias: null, desc: 'ETF flows' },
  { cmd: '/contracts', alias: null, desc: 'Gov contracts' },
  { cmd: '/patents', alias: null, desc: 'Patents' },
  { cmd: '/lobbying', alias: null, desc: 'Lobbying' },
  // Market Structure
  { cmd: '/cacs', alias: 'CACS', desc: 'Corp. actions' },
  { cmd: '/spread', alias: null, desc: 'Bid/ask spread' },
  { cmd: '/float', alias: null, desc: 'Float & ownership' },
  // Options
  { cmd: '/options-flow', alias: '/oflow OMON OV', desc: 'Options flow' },
  { cmd: '/pcr', alias: null, desc: 'Put/call ratio' },
  { cmd: '/maxpain', alias: null, desc: 'Max pain' },
  // Portfolio
  { cmd: '/portfolio', alias: '/pf PORT', desc: 'Portfolio' },
  { cmd: '/positions', alias: '/pos PMON', desc: 'Positions' },
  { cmd: '/risk', alias: 'MARS VAR', desc: 'Risk dashboard' },
  { cmd: '/stress', alias: null, desc: 'Stress test' },
  { cmd: '/pot', alias: null, desc: 'Disc. pot' },
  { cmd: '/pnl', alias: null, desc: 'P&L summary' },
  { cmd: '/goals', alias: null, desc: 'Goals' },
  { cmd: '/watchlist', alias: '/wl', desc: 'Watchlist' },
  // Analysis
  { cmd: '/tra', alias: null, desc: 'Trade analysis' },
  // Agents
  { cmd: '/analyze', alias: '/run', desc: 'Agent analysis' },
  { cmd: '/recommendations', alias: '/recs', desc: 'Recommendations' },
  { cmd: '/consensus', alias: null, desc: 'Consensus' },
  // Dashboards
  { cmd: '/ftse100', alias: 'UKX', desc: 'FTSE 100 dashboard' },
  { cmd: '/sp500', alias: 'SPX', desc: 'S&P 500 dashboard' },
  { cmd: '/nasdaq', alias: null, desc: 'NASDAQ dashboard' },
  { cmd: '/sector', alias: null, desc: 'Sector deep-dive' },
  { cmd: '/macro', alias: 'ECO ECFC WEI', desc: 'Macro dashboard' },
  { cmd: '/pd', alias: null, desc: 'Portfolio dashboard' },
  { cmd: '/screener', alias: 'GRAB EQS SECF RV', desc: 'Screener' },
  { cmd: '/scan', alias: null, desc: 'Quick scan preset' },
  { cmd: '/news', alias: 'TOP N NI CN', desc: 'Headlines' },
  { cmd: '/calendar', alias: 'EVTS', desc: 'Events calendar' },
  { cmd: '/ipo', alias: null, desc: 'Upcoming IPOs' },
  // System
  { cmd: '/help', alias: 'HELP', desc: 'Help' },
  { cmd: '/status', alias: null, desc: 'Status' },
  { cmd: '/clear', alias: null, desc: 'Clear' },
  // Bloomberg shortcut aliases
  { cmd: '/des', alias: null, desc: '-> /profile' },
  { cmd: '/ta', alias: null, desc: '-> /technicals' },
  { cmd: '/mov', alias: null, desc: '-> /movers' },
  { cmd: '/gb', alias: null, desc: '-> /bonds' },
  { cmd: '/bq', alias: null, desc: '-> /quote' },
]

// ── Auto-refresh intervals ───────────────────────────────────────────────

const REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '30s', value: 30 },
  { label: '1m', value: 60 },
  { label: '2m', value: 120 },
  { label: '5m', value: 300 },
]

// ── Mini terminal panel ──────────────────────────────────────────────────

function TerminalPanel({
  panel,
  isMaximized,
  onMaximize,
  onClose,
  onSwapStart,
  onPopOut,
  swapSource,
}: {
  panel: PanelConfig
  isMaximized: boolean
  onMaximize: (id: string | null) => void
  onClose: (id: string) => void
  onSwapStart: (id: string) => void
  onPopOut: (id: string) => void
  swapSource: string | null
}) {
  const [input, setInput] = useState('')
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedSuggestion, setSelectedSuggestion] = useState(0)
  const [autoRefresh, setAutoRefresh] = useState(panel.autoRefresh ?? 120) // default 2 min
  const [lastCommand, setLastCommand] = useState(panel.initialCommand ?? '')

  const inputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const initDone = useRef(false)
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { terminals, createTerminal, executeCommand, clearTerminal } = useMultiTerminalStore()
  const terminal = terminals[panel.terminalId]
  const history = terminal?.history ?? []
  const commandHistory = terminal?.commandHistory ?? []
  const executing = terminal?.executing ?? false
  const { updatePanelAutoRefresh } = useWorkspaceStore()

  // Create terminal instance and run initial command
  useEffect(() => {
    createTerminal(panel.terminalId)
    if (!initDone.current && panel.initialCommand) {
      initDone.current = true
      setTimeout(() => {
        executeCommand(panel.terminalId, panel.initialCommand!)
        setLastCommand(panel.initialCommand!)
      }, 200)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh
  useEffect(() => {
    if (autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current)
      autoRefreshRef.current = null
    }
    if (autoRefresh > 0 && lastCommand) {
      autoRefreshRef.current = setInterval(() => {
        executeCommand(panel.terminalId, lastCommand)
      }, autoRefresh * 1000)
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current)
    }
  }, [autoRefresh, lastCommand, panel.terminalId, executeCommand])

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    }
  }, [history])

  // Autocomplete
  const suggestions = useMemo(() => {
    if (!input.startsWith('/') || input.includes(' ')) return []
    const query = input.toLowerCase()
    return ALL_COMMANDS.filter(
      c => c.cmd.startsWith(query) || (c.alias && c.alias.split(' ').some(a => a.toLowerCase().startsWith(query) || ('/' + a.toLowerCase()).startsWith(query)))
    ).slice(0, 6)
  }, [input])

  useEffect(() => {
    setShowSuggestions(suggestions.length > 0 && input.length > 0)
    setSelectedSuggestion(0)
  }, [suggestions, input])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.ctrlKey && e.key === 'l') {
        e.preventDefault()
        clearTerminal(panel.terminalId)
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        if (input.trim()) {
          const cmd = input.trim()
          executeCommand(panel.terminalId, cmd)
          setLastCommand(cmd)
          setInput('')
          setHistoryIndex(-1)
          setShowSuggestions(false)
        }
        return
      }

      if (e.key === 'Tab') {
        e.preventDefault()
        if (suggestions.length > 0) {
          const selected = suggestions[selectedSuggestion]
          setInput(selected.cmd + ' ')
          setShowSuggestions(false)
        }
        return
      }

      if (showSuggestions && suggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSelectedSuggestion(prev => Math.min(prev + 1, suggestions.length - 1))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSelectedSuggestion(prev => Math.max(prev - 1, 0))
          return
        }
      }

      if (e.key === 'ArrowUp' && !showSuggestions) {
        e.preventDefault()
        if (commandHistory.length === 0) return
        const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1)
        setHistoryIndex(newIndex)
        setInput(commandHistory[newIndex])
        return
      }
      if (e.key === 'ArrowDown' && !showSuggestions) {
        e.preventDefault()
        if (historyIndex === -1) return
        const newIndex = historyIndex + 1
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1)
          setInput('')
        } else {
          setHistoryIndex(newIndex)
          setInput(commandHistory[newIndex])
        }
        return
      }

      if (e.key === 'Escape') {
        setShowSuggestions(false)
        return
      }
    },
    [input, showSuggestions, suggestions, selectedSuggestion, commandHistory, historyIndex,
     executeCommand, clearTerminal, panel.terminalId],
  )

  const lineClass = (line: TerminalLine) => {
    switch (line.type) {
      case 'command': return 'text-crt-bright'
      case 'error': return 'text-red-400'
      case 'system': return 'text-crt-dim'
      case 'quote': return 'text-crt-amber'
      case 'chart': return 'text-crt-cyan'
      case 'dashboard': return 'text-crt-green'
      case 'table': return 'text-crt-green'
      default: return 'text-crt-green'
    }
  }

  const handleAutoRefreshChange = (val: number) => {
    setAutoRefresh(val)
    updatePanelAutoRefresh(panel.id, val || undefined)
  }

  return (
    <div
      className={`flex flex-col bg-black border border-crt-green/30 rounded overflow-hidden relative
                  ${isMaximized ? 'fixed inset-4 z-50' : 'h-full'}
                  crt-panel-glow`}
      onClick={() => inputRef.current?.focus()}
    >
      {/* CRT overlay per panel */}
      <div className="absolute inset-0 pointer-events-none crt-scanlines opacity-20" aria-hidden />

      {/* Panel header */}
      <div className="flex items-center justify-between px-2 py-1 border-b border-crt-green/20 bg-black/80 shrink-0 z-10">
        <div className="flex items-center gap-2">
          <span className="text-crt-green font-mono text-xs font-bold truncate max-w-[140px]">
            {lastCommand || 'TERMINAL'}
          </span>
          {executing && <span className="text-crt-amber font-mono text-[10px] animate-pulse">EXEC</span>}
        </div>
        <div className="flex items-center gap-1">
          {/* Auto-refresh selector */}
          <select
            value={autoRefresh}
            onChange={(e) => handleAutoRefreshChange(Number(e.target.value))}
            className="bg-black text-crt-green font-mono text-[11px] border border-crt-green/30
                       rounded px-1.5 py-0.5 outline-none cursor-pointer hover:border-crt-green/60
                       focus:ring-1 focus:ring-crt-green/40"
            title="Auto-refresh interval"
          >
            {REFRESH_OPTIONS.map(o => (
              <option key={o.value} value={o.value} className="bg-black">{o.label}</option>
            ))}
          </select>
          {/* Swap button */}
          <button
            onClick={(e) => { e.stopPropagation(); onSwapStart(panel.id) }}
            className={`text-crt-dim hover:text-crt-green text-xs px-1 font-mono transition-colors
                        ${swapSource === panel.id ? 'text-crt-amber' : ''}`}
            title={swapSource ? 'Click to swap with this panel' : 'Click to start swap'}
          >
            {swapSource && swapSource !== panel.id ? '[ SWAP ]' : '<>'}
          </button>
          {/* Pop-out button */}
          <button
            onClick={(e) => { e.stopPropagation(); onPopOut(panel.id) }}
            className="text-crt-dim hover:text-crt-green text-xs px-1 font-mono transition-colors"
            title="Pop out to new window"
          >
            <ArrowUpRight size={12} />
          </button>
          {/* Maximize/minimize */}
          <button
            onClick={(e) => { e.stopPropagation(); onMaximize(isMaximized ? null : panel.id) }}
            className="text-crt-dim hover:text-crt-green text-xs px-1 font-mono transition-colors"
            title={isMaximized ? 'Minimize' : 'Maximize'}
          >
            {isMaximized ? '[-]' : '[+]'}
          </button>
          {/* Close */}
          <button
            onClick={(e) => { e.stopPropagation(); onClose(panel.id) }}
            className="text-crt-dim hover:text-red-400 text-xs px-1 font-mono transition-colors"
            title="Close panel"
          >
            [x]
          </button>
        </div>
      </div>

      {/* Terminal output */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-2 py-1 font-mono text-xs leading-relaxed crt-scrollbar min-h-0"
        role="log"
      >
        {history.map(line => (
          <div key={line.id} className="py-px">
            {line.type === 'command' ? (
              <div className="flex gap-1">
                <span className="text-crt-dim shrink-0">[{line.timestamp}]</span>
                <span className="text-crt-bright shrink-0">{'>'}</span>
                <span className="text-crt-bright">{line.content}</span>
              </div>
            ) : line.type === 'system' ? (
              <div className="flex gap-1">
                <span className="text-crt-dim shrink-0">SYS</span>
                <span className="text-crt-dim whitespace-pre">{line.content}</span>
              </div>
            ) : (
              <div className={`whitespace-pre ${lineClass(line)}`}>
                {line.content}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Autocomplete suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="px-2 pb-0.5 border-t border-crt-green/10 shrink-0 z-10">
          <div className="font-mono text-[10px] py-0.5 flex flex-wrap gap-x-2 gap-y-0">
            {suggestions.map((s, i) => (
              <button
                key={s.cmd}
                type="button"
                onClick={() => {
                  setInput(s.cmd + ' ')
                  setShowSuggestions(false)
                  inputRef.current?.focus()
                }}
                className={`px-0.5 rounded transition-colors ${
                  i === selectedSuggestion
                    ? 'text-crt-bright bg-crt-green/20'
                    : 'text-crt-dim hover:text-crt-green'
                }`}
              >
                {s.cmd}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Command input */}
      <div className="flex items-center gap-1 px-2 py-1 border-t border-crt-green/20 bg-black/50 shrink-0 z-10">
        <span className="text-crt-green font-mono text-xs font-bold shrink-0">
          {executing ? '...' : '>'}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => { setInput(e.target.value); setHistoryIndex(-1) }}
          onKeyDown={handleKeyDown}
          placeholder={executing ? 'executing...' : '/command'}
          disabled={executing}
          spellCheck={false}
          autoComplete="off"
          className="flex-1 bg-transparent font-mono text-xs text-crt-green placeholder-crt-dim/50
                     border-none outline-none caret-crt-green disabled:opacity-50 min-w-0"
          aria-label="Terminal command input"
        />
      </div>
    </div>
  )
}

// ── Workspace Page ───────────────────────────────────────────────────────

export default function WorkspacePage() {
  const {
    layout, panels, savedLayouts, maximizedPanel,
    switchLayout, loadPreset, saveLayout, loadSavedLayout, deleteLayout,
    swapPanels, maximizePanel, closePanel,
  } = useWorkspaceStore()

  const [swapSource, setSwapSource] = useState<string | null>(null)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [showPresets, setShowPresets] = useState(false)
  const [showSaved, setShowSaved] = useState(false)

  // BroadcastChannel for cross-window panel lifecycle events
  const broadcastRef = useRef<BroadcastChannel | null>(null)
  useEffect(() => {
    broadcastRef.current = new BroadcastChannel('workspace-sync')
    return () => broadcastRef.current?.close()
  }, [])

  const handlePopOut = useCallback((panelId: string) => {
    const panel = panels.find(p => p.id === panelId)
    if (!panel) return
    const params = new URLSearchParams({
      command: panel.initialCommand || '',
      autoRefresh: String(panel.autoRefresh || 0),
      terminalId: panel.terminalId,
      panelId: panel.id,
    })
    window.open(
      `/app/workspace/panel?${params.toString()}`,
      `panel-${panelId}`,
      'width=900,height=700,menubar=no,toolbar=no,status=no',
    )
  }, [panels])

  const handleClosePanel = useCallback((panelId: string) => {
    broadcastRef.current?.postMessage({ type: 'panel-removed', panelId })
    closePanel(panelId)
  }, [closePanel])

  const handleSwapStart = useCallback((panelId: string) => {
    if (swapSource === null) {
      setSwapSource(panelId)
    } else if (swapSource === panelId) {
      setSwapSource(null)
    } else {
      swapPanels(swapSource, panelId)
      setSwapSource(null)
    }
  }, [swapSource, swapPanels])

  const handleSave = useCallback(() => {
    if (saveName.trim()) {
      saveLayout(saveName.trim())
      setSaveName('')
      setSaveDialogOpen(false)
    }
  }, [saveName, saveLayout])

  const handleLoadSaved = useCallback((name: string) => {
    loadSavedLayout(name)
    setShowSaved(false)
  }, [loadSavedLayout])

  const gridConfig = LAYOUT_CONFIGS[layout]

  // Grid template for CSS grid
  const gridStyle = useMemo(() => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${gridConfig.cols}, 1fr)`,
    gridTemplateRows: `repeat(${gridConfig.rows}, 1fr)`,
    gap: '4px',
    height: '100%',
  }), [gridConfig])

  // If a panel is maximized, only show that panel
  const visiblePanels = maximizedPanel
    ? panels.filter(p => p.id === maximizedPanel)
    : panels

  return (
    <div className="flex flex-col bg-black animate-fade-in overflow-hidden -mx-4 md:-mx-6 -my-5 md:-my-6" style={{ height: 'calc(100vh - 120px)' }}>
      {/* Workspace toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-crt-green/30 bg-black/80 shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-crt-green font-mono text-sm font-bold">WORKSPACE</span>

          {/* Layout selector */}
          <div className="flex items-center gap-1">
            {(Object.keys(LAYOUT_CONFIGS) as WorkspaceLayout[]).map(l => (
              <button
                key={l}
                onClick={() => switchLayout(l)}
                className={`px-2 py-1 font-mono text-xs rounded transition-colors
                  ${layout === l
                    ? 'text-crt-bright bg-crt-green/20 border border-crt-green/40'
                    : 'text-crt-dim border border-transparent hover:text-crt-green hover:border-crt-green/20'
                  }`}
                title={LAYOUT_CONFIGS[l].label}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Presets dropdown */}
          <div className="relative">
            <button
              onClick={() => { setShowPresets(!showPresets); setShowSaved(false) }}
              className="px-2 py-1 font-mono text-xs text-crt-dim border border-crt-green/20
                         rounded hover:text-crt-green hover:border-crt-green/40 transition-colors"
            >
              PRESETS
            </button>
            {showPresets && (
              <div className="absolute right-0 top-full mt-1 bg-black border border-crt-green/30
                              rounded shadow-lg shadow-crt-green/10 z-50 min-w-[180px]">
                {WORKSPACE_PRESETS.map(preset => (
                  <button
                    key={preset.name}
                    onClick={() => { loadPreset(preset.name); setShowPresets(false) }}
                    className="w-full text-left px-3 py-2 font-mono text-xs text-crt-dim
                               hover:text-crt-green hover:bg-crt-green/10 transition-colors"
                  >
                    {preset.name}
                    <span className="text-crt-dim/50 ml-2">({preset.layout})</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Saved layouts dropdown */}
          <div className="relative">
            <button
              onClick={() => { setShowSaved(!showSaved); setShowPresets(false) }}
              className="px-2 py-1 font-mono text-xs text-crt-dim border border-crt-green/20
                         rounded hover:text-crt-green hover:border-crt-green/40 transition-colors"
            >
              SAVED
            </button>
            {showSaved && (
              <div className="absolute right-0 top-full mt-1 bg-black border border-crt-green/30
                              rounded shadow-lg shadow-crt-green/10 z-50 min-w-[200px]">
                {Object.keys(savedLayouts).length === 0 ? (
                  <div className="px-3 py-2 font-mono text-xs text-crt-dim/50">
                    No saved layouts
                  </div>
                ) : (
                  Object.entries(savedLayouts).map(([name, saved]) => (
                    <div key={name} className="flex items-center justify-between px-3 py-2 hover:bg-crt-green/10">
                      <button
                        onClick={() => handleLoadSaved(name)}
                        className="font-mono text-xs text-crt-dim hover:text-crt-green transition-colors text-left flex-1"
                      >
                        {name}
                        <span className="text-crt-dim/50 ml-2">({saved.layout})</span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteLayout(name) }}
                        className="text-crt-dim/50 hover:text-red-400 font-mono text-xs ml-2 transition-colors"
                      >
                        x
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Save button */}
          <div className="relative">
            <button
              onClick={() => setSaveDialogOpen(!saveDialogOpen)}
              className="px-2 py-1 font-mono text-xs text-crt-dim border border-crt-green/20
                         rounded hover:text-crt-green hover:border-crt-green/40 transition-colors"
            >
              SAVE
            </button>
            {saveDialogOpen && (
              <div className="absolute right-0 top-full mt-1 bg-black border border-crt-green/30
                              rounded shadow-lg shadow-crt-green/10 z-50 p-3">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={saveName}
                    onChange={e => setSaveName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSave() }}
                    placeholder="Layout name..."
                    className="bg-transparent font-mono text-xs text-crt-green placeholder-crt-dim/50
                               border border-crt-green/30 rounded px-2 py-1 outline-none w-[140px]"
                    autoFocus
                  />
                  <button
                    onClick={handleSave}
                    className="px-2 py-1 font-mono text-xs text-crt-bright bg-crt-green/20
                               border border-crt-green/40 rounded hover:bg-crt-green/30 transition-colors"
                  >
                    OK
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Swap mode indicator */}
          {swapSource && (
            <span className="text-crt-amber font-mono text-xs animate-pulse">
              SWAP MODE -- click target panel
            </span>
          )}
        </div>
      </div>

      {/* Panel grid */}
      <div className="flex-1 p-1 overflow-hidden min-h-0" style={maximizedPanel ? undefined : gridStyle}>
        {maximizedPanel ? (
          /* Maximized view: single panel fills everything */
          <div className="h-full">
            {visiblePanels.map(panel => (
              <TerminalPanel
                key={panel.terminalId}
                panel={panel}
                isMaximized={true}
                onMaximize={maximizePanel}
                onClose={handleClosePanel}
                onSwapStart={handleSwapStart}
                onPopOut={handlePopOut}
                swapSource={swapSource}
              />
            ))}
          </div>
        ) : (
          /* Normal grid view */
          visiblePanels.map(panel => (
            <TerminalPanel
              key={panel.terminalId}
              panel={panel}
              isMaximized={false}
              onMaximize={maximizePanel}
              onClose={handleClosePanel}
              onSwapStart={handleSwapStart}
              onPopOut={handlePopOut}
              swapSource={swapSource}
            />
          ))
        )}
      </div>
    </div>
  )
}
